import React, { FC, useRef } from 'react';

import { IconCTA } from 'Src/components/buttons/IconCTA';
import { Flex } from 'Src/components/layout/Flex';

import { CheckItemSeatSelectionCTA, CheckItemSeatSelectionCTAProps } from './CheckItemSeatSelectionCTA';
import { Table, useSeatAssignment } from './useSeatAssignment';

import styles from './CheckItemSeatSelection.module.scss';

const sectionId = 'CheckItemSeatSelection';

const CheckItemSeatSelection: FC = () => {
  const seatAssignment = useSeatAssignment();
  const optionsScrollContainerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const onUI: CheckItemSeatSelectionCTAProps['onUI'] = ({ model, mode }) => {
    const isSelected = seatAssignment.selections.isSelected(model);
    if (model instanceof Table) {
      // TODO: what to do w/ longpress of table
      seatAssignment.selections.replace([model.uid]);
      // scroll all the way back to the left
      optionsScrollContainerRef.current?.scroll({ left: 0, behavior: 'smooth' });
    } else {
      if (mode === 'click') {
        // only select one at a time
        seatAssignment.selections.replace([model.uid]);
      } else if (mode === 'longpress') {
        // always remove the table while seat long press
        let selections = seatAssignment.selections.current.filter((uid) => uid !== seatAssignment.table.uid);
        // select multiple at a time
        // already selected
        if (isSelected) {
          // remove id from selection set
          selections = selections.filter((uid) => uid === model.uid);
        } else {
          // append id to selection set
          selections.push(model.uid);
        }
        seatAssignment.selections.replace(selections);
      }
    }
  };

  const onAddMoreClick = () => {
    seatAssignment.seats.add();
    // skip a render to measure the container after a new element has been added
    setTimeout(() => {
      const containerRect = optionsContainerRef.current?.getBoundingClientRect();
      // scroll to the left most bounds
      const left = containerRect ? containerRect.width : 0;
      optionsScrollContainerRef.current?.scroll({ left, behavior: 'smooth' });
    }, 10);
  };

  return (
    <Flex
      data-testid={sectionId}
      gap="sm"
      padding="sm"
      horizontal
      fullWidth
    >
      <CheckItemSeatSelectionCTA
        model={seatAssignment.table}
        selected={seatAssignment.selections.isSelected(seatAssignment.table)}
        onUI={onUI}
      />
      <div className={styles.optionsScrollContainer} ref={optionsScrollContainerRef}>
        <div className={styles.optionsContainer} ref={optionsContainerRef}>
          {seatAssignment.seats.list.map((seatModel) => (
            <CheckItemSeatSelectionCTA
              key={seatModel.uid}
              model={seatModel}
              selected={seatAssignment.selections.isSelected(seatModel)}
              onUI={onUI}
            />
          ))}
        </div>
      </div>
      <IconCTA
        variant="outlined"
        icon="add"
        onClick={onAddMoreClick}
      />
    </Flex>
  );
};

export { CheckItemSeatSelection, sectionId };
